import {
  ActionIcon,
  Divider,
  Drawer,
  DrawerProps,
  Group,
  Stack,
} from "@mantine/core";
import { IconChevronRight, IconX } from "@tabler/icons-react";
import React, { useEffect, useRef } from "react";

import { Text, Title } from "@components";

const contactInfo = [
  {
    label: "Email",
    value: "info@airtightdesign.com",
    href: "mailto:info@airtightdesign.com",
  },
  {
    label: "Phone",
    value: "(404) 594-5520",
    href: "tel:4045945520",
  },
];

interface ContactUsDrawerProps extends Omit<DrawerProps, "children"> {
  onBack?: () => void;
  onCloseStack?: () => void;
}

export function ContactUsDrawer({
  onBack,
  onCloseStack,
  ...drawerProps
}: ContactUsDrawerProps) {
  const hubspotFormRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (
      !document.querySelector(
        'script[src="https://js.hsforms.net/forms/embed/46087357.js"]'
      )
    ) {
      const script = document.createElement("script");
      script.src = "https://js.hsforms.net/forms/embed/46087357.js";
      script.defer = true;
      document.body.appendChild(script);
    }

    const style = document.createElement("style");
    style.textContent = `
      .hsfc-Step__Content {
        padding: 0 !important;
      }
    `;
    document.head.appendChild(style);

    return () => {
      document.head.removeChild(style);
    };
  }, []);

  return (
    <Drawer
      position="right"
      size="lg"
      padding="xl"
      offset={12}
      radius="sm"
      overlayProps={{ backgroundOpacity: onBack ? 0 : 0.9 }}
      closeButtonProps={{
        bg: "transparent",
        icon: <IconX size={24} />,
        size: "lg",
        onClick: onCloseStack,
      }}
      styles={{
        header: {
          paddingTop: "var(--mantine-spacing-lg)",
          paddingBottom: "var(--mantine-spacing-lg)",
          paddingLeft: onBack ? "var(--mantine-spacing-md)" : undefined,
          borderBottom: "1px solid var(--mantine-color-decorativeBorder-0)",
          background:
            "linear-gradient(270deg, var(--mantine-color-layoutLight-0) 0%, var(--mantine-color-layoutLight-0) 100%)",
          boxShadow: "inset 0 1px 1px 0 rgb(255 255 255 / 25%)",
        },
        body: {
          paddingTop: "var(--mantine-spacing-xl)",
          background:
            "linear-gradient(270deg, var(--mantine-color-layoutLight-0) 0%, var(--mantine-color-layoutLight-0) 100%)",
        },
        content: {
          background:
            "linear-gradient(270deg, var(--mantine-color-layoutLight-0) 0%, var(--mantine-color-layoutLight-0) 100%)",
          boxShadow: "0 1px 1px 0 rgb(0 0 0 / 25%)",
          overflowX: "hidden",
        },
        inner: {
          right: 0,
        },
        close: {
          color: "var(--mantine-color-fontDark-0)",
        },
      }}
      title={
        onBack ? (
          <Group>
            <ActionIcon
              variant="subtle"
              onClick={onBack}
              size="lg"
              radius="xl"
              styles={{
                root: {
                  color: "var(--mantine-color-fontDark-0)",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.1)",
                  },
                },
              }}
            >
              <IconChevronRight
                size={24}
                style={{ transform: "rotate(180deg)" }}
              />
            </ActionIcon>
            <Title order={5} c="fontDark">
              Contact&nbsp;Us
            </Title>
          </Group>
        ) : (
          <Title order={5} c="fontDark">
            Contact Us
          </Title>
        )
      }
      {...drawerProps}
    >
      <Stack gap="lg">
        <Stack gap="md" hiddenFrom="xs">
          {contactInfo.map((item) => (
            <Stack key={item.label} gap="xs">
              <Text c="fontDark" fw="600" size="xs">
                {item.label}
              </Text>
              <Text
                c="fontDark"
                size="xs"
                {...(item.href && { component: "a", href: item.href })}
              >
                {item.value}
              </Text>
            </Stack>
          ))}
        </Stack>

        <Group grow gap="md" visibleFrom="xs">
          {contactInfo.map((item) => (
            <Stack key={item.label} gap="xs">
              <Text c="fontDark" fw="600" size="xs">
                {item.label}
              </Text>
              <Text
                c="fontDark"
                size="xs"
                {...(item.href && { component: "a", href: item.href })}
              >
                {item.value}
              </Text>
            </Stack>
          ))}
        </Group>

        <Divider color="decorativeBorder" my="sm" />

        <Text size="xs">
          We’d love to learn more about what you’re working on. Share a few
          details in the form below, and our team will be in touch soon.
        </Text>

        <div
          ref={hubspotFormRef}
          className="hs-form-frame"
          data-region="na1"
          data-form-id="c9a61dcd-19ac-4ed6-8830-65a1e04bd0e1"
          data-portal-id="46087357"
          style={{
            margin: -40,
          }}
        />
      </Stack>
    </Drawer>
  );
}
